import {Component, Input, OnInit} from '@angular/core';
import {DcpPriceCommitmentPeriodData} from '@app/model/price/dcp-price-commitment-period-data';
import {DcpPriceIncludesData} from '@app/model/price/dcp-price-includes-data';
import {DcpPriceRestrictionsData} from '@app/model/price/dcp-price-restrictions-data';
import {DcpPriceServicePlanData} from '@app/model/price/dcp-price-service-plan-data';
import {TermsConditionsService} from '@app/services/terms-conditions.service';
import {CheckmarkListEntry} from '@app/shared/checkmark-list/checkmark-list.component';
import {DcpBundleTemplate} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslationService, TranslatePipe} from '@spartacus/core';
import {CurrentProductService} from '@spartacus/storefront';
import {combineLatest} from 'rxjs';
import {map, switchMap, take} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../../services/dcp-product-utilities.service';
import {CheckmarkListComponent} from '../../../../shared/checkmark-list/checkmark-list.component';
import {TermsAndConditionsComponent} from '../../../../checkout/checkout-terms-conditions/terms-and-conditions.component';
import {ProductPageDecoratorService} from '@app/product/services/product-page-decorator.service';
import {OrderEntry} from '@spartacus/cart/base/root';

@UntilDestroy()
@Component({
  selector: 'dcp-plans-and-pricing-features',
  templateUrl: './plans-and-pricing-features.component.html',
  imports: [CheckmarkListComponent, TermsAndConditionsComponent, TranslatePipe],
})
export class PlansAndPricingFeaturesComponent implements OnInit {
  @Input() servicePlan: DcpPriceServicePlanData;
  @Input() commitmentPeriod: DcpPriceCommitmentPeriodData;
  @Input() restrictions: DcpPriceRestrictionsData[];
  @Input() bundleTemplate: DcpBundleTemplate;
  @Input() hasContentForFeatureSection: boolean = false;
  includedList: CheckmarkListEntry[];
  termsConditionsProductEntries: OrderEntry[] = [];

  constructor(
    private translationService: TranslationService,
    private currentProductService: CurrentProductService,
    private termsConditionsService: TermsConditionsService,
    private dcpProductUtilitiesService: DcpProductUtilitiesService,
    private productPageDecoratorService: ProductPageDecoratorService,
  ) {}

  ngOnInit(): void {
    this.currentProductService
      .getProduct()
      .pipe(
        take(1),
        switchMap((product) => {
         return this.productPageDecoratorService.getCtaProductDetermination(product ?? {}).pipe(
          map((ctaProductDetermination) => {
            const entitlements  = ctaProductDetermination?.entitlements;
            return  {product, entitlements}
          })
         )
        }), 
        untilDestroyed(this))
      .subscribe(({product, entitlements}) => {
        if (this.dcpProductUtilitiesService.isSapProduct(product)) {
          this.termsConditionsProductEntries = this.termsConditionsService.createTermsConditionsEntriesFromProduct(
            product,
            entitlements,
            this.bundleTemplate?.id
          );
        }
      });
  }

  @Input() set includes(value: DcpPriceIncludesData[]) {
    if (value) {
      combineLatest([
        this.translationService.translate('productDetails.includes'),
        this.translationService.translate('productDetails.unlimited'),
      ])
        .pipe(take(1), untilDestroyed(this))
        .subscribe(([includes, unlimited]) => {
          this.includedList = value.map((entry) => ({
            value: includes + ' ' + (entry.unlimited ? unlimited : entry.amount) + ' ' + entry.metric.name,
          }));
        });
    } else {
      this.includedList = [];
    }
  }

  get durationTranslationKey(): string {
    if (this.commitmentPeriod.max && this.commitmentPeriod.min) {
      return this.commitmentPeriod.max === this.commitmentPeriod.min
        ? 'productDetails.oneDurationOnly'
        : 'productDetails.minMaxDuration';
    }

    if (this.commitmentPeriod.max) {
      return 'productDetails.maxDuration';
    }

    return 'productDetails.minDuration';
  }

  mergeArrays(array1: {name?: string; value: string}[], array2: {name?: string; value: string}[]) {
    if (array1 && array1.length) {
      if (array2 && array2.length) {
        return [...array1, ...array2];
      }
      return array1;
    }
    return array2;
  }

  onNoEntryData($event: any) {
    if (this.bundleTemplate?.id === $event.product?.id) {
      this.termsConditionsProductEntries = [];
    }
  }
}
